import { AlertTriangle, ArrowLeftRight, CheckCircle2, Download, Upload, Users } from 'lucide-react';
import { Boxes } from 'lucide-react';
import * as React from 'react';
import { toast } from 'sonner';

import {
  Alert,
  AlertDescription,
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/cargofl-ui';
import { PageContainer } from '@/components/shell/page-container';
import { downloadTemplate, useImport, type ImportKind, type ImportReport } from '@/lib/admin-api';
import { ApiError } from '@/lib/api';
import { useCan } from '@/lib/session';

interface ImporterSpec {
  kind: ImportKind;
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  required: string;
  optional: string;
  /** What this import will NOT do, said up front. */
  caveat: string;
  permission: 'manageEmployees' | 'manageAssets' | 'assignAssets';
}

/**
 * The three importers.
 *
 * Ordered by the sequence they have to be run in for a fresh system - people, then
 * hardware, then who has what - because the third depends on both of the others
 * existing, and a page that listed them alphabetically would invite someone to start
 * with assignments and get 200 "no such employee" errors.
 */
const IMPORTERS: ImporterSpec[] = [
  {
    kind: 'employees',
    title: 'Employees',
    icon: Users,
    required: 'Employee ID, Employee Name',
    optional: 'Department, Designation, Email, Phone, Joining Date, Status, Notes',
    caveat:
      'Everyone is imported as a plain Employee. Roles are granted one at a time in Settings — a spreadsheet column that could hand out IT rights is one careless paste away from a problem.',
    permission: 'manageEmployees',
  },
  {
    kind: 'assets',
    title: 'Assets',
    icon: Boxes,
    required: 'Asset Type, Asset Name',
    optional:
      'Brand, Model, Serial Number, Purchase Date, Vendor, Invoice Number, Purchase Price, Warranty Start, Warranty End, Status, Ownership, Notes',
    caveat:
      'Asset codes are generated here, in sequence — there is no Asset Code column. Ownership defaults to company-owned, and an employee-owned row gets the second prefix. An asset cannot be imported as Assigned; register it, then use the assignment import.',
    permission: 'manageAssets',
  },
  {
    kind: 'assignments',
    title: 'Assignments',
    icon: ArrowLeftRight,
    required: 'Asset Code, Employee ID',
    optional: 'Assignment Date (defaults to today), Condition (defaults to Good), Remarks',
    caveat:
      'Run this after the other two. Each row is matched against the register, so an asset that is already out, or an employee who has left, is reported by line rather than silently skipped.',
    permission: 'assignAssets',
  },
];

export function ImportPage() {
  return (
    <PageContainer>
      <Alert>
        <AlertDescription className="text-sm">
          <span className="font-medium">Download the template first.</span> Columns are matched
          by heading, so you can reorder them or leave optional ones out — but the heading row
          has to be there. Every rejected row is reported against the line number Excel shows
          in its gutter, and nothing is written unless the whole file can be.
        </AlertDescription>
      </Alert>

      <div className="grid gap-4 lg:grid-cols-3">
        {IMPORTERS.map((spec) => (
          <Importer key={spec.kind} spec={spec} />
        ))}
      </div>
    </PageContainer>
  );
}

function Importer({ spec }: { spec: ImporterSpec }) {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [report, setReport] = React.useState<ImportReport | null>(null);
  const canImport = useCan(spec.permission);
  const importFile = useImport(spec.kind);

  const onFile = async (file: File | undefined) => {
    if (!file) return;
    setReport(null);
    try {
      const result = await importFile.mutateAsync(file);
      setReport(result);
      if (result.clean) {
        toast.success(`${result.imported} row${result.imported === 1 ? '' : 's'} imported.`);
      } else if (result.imported > 0) {
        toast.warning(`${result.imported} imported, ${result.issues.length} skipped.`);
      } else {
        toast.error('Nothing was imported. See the issues listed.');
      }
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : 'That file could not be read.');
    } finally {
      // Cleared so choosing the SAME file again still fires a change event - which is
      // exactly what someone does after fixing two rows in it.
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <Card className="flex flex-col">
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <spec.icon className="size-4 text-primary" />
          <CardTitle className="text-sm">{spec.title}</CardTitle>
        </div>
        <CardDescription className="text-xs leading-relaxed">
          <span className="font-medium text-foreground">Required:</span> {spec.required}
          <br />
          <span className="font-medium text-foreground">Optional:</span> {spec.optional}
        </CardDescription>
      </CardHeader>

      <CardContent className="flex flex-1 flex-col gap-3 pt-0">
        <p className="rounded-md bg-muted/60 p-2.5 text-[11px] leading-relaxed text-muted-foreground">
          {spec.caveat}
        </p>

        <div className="mt-auto flex flex-wrap gap-2">
          <Button hint={"Download a blank spreadsheet with the right columns"}
            size="sm"
            variant="secondary"
            onClick={() => {
              void downloadTemplate(spec.kind).catch(() =>
                toast.error('The template could not be downloaded.'),
              );
            }}
          >
            <Download className="mr-1.5 size-4" /> Template
          </Button>

          <Button hint={"Choose the filled-in spreadsheet to import"}
            size="sm"
            disabled={!canImport || importFile.isPending}
            onClick={() => inputRef.current?.click()}
          >
            <Upload className="mr-1.5 size-4" />
            {importFile.isPending ? 'Importing…' : 'Choose CSV'}
          </Button>

          <input
            ref={inputRef}
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            onChange={(e) => void onFile(e.target.files?.[0])}
          />
        </div>

        {!canImport && (
          <p className="text-[11px] text-muted-foreground">
            You can download the template, but only{' '}
            {spec.permission === 'manageEmployees' ? 'HR' : 'IT'} or an administrator can
            upload it.
          </p>
        )}

        {report && <ImportResult report={report} />}
      </CardContent>
    </Card>
  );
}

/**
 * The result of a run.
 *
 * `imported` and `issues` together account for every non-blank row in the file, so it
 * can be reconciled against the spreadsheet on screen. The prototype reported a bare
 * "42 imported · 8 skipped" with no word on WHICH eight - which is what makes people
 * re-upload the whole file and create duplicates.
 */
function ImportResult({ report }: { report: ImportReport }) {
  if (report.clean) {
    return (
      <Alert className="border-success/30 bg-success/5">
        <AlertDescription className="flex items-center gap-2 text-xs">
          <CheckCircle2 className="size-4 shrink-0 text-success" />
          <span>
            <span className="font-medium">{report.imported}</span> row
            {report.imported === 1 ? '' : 's'} imported. Every row was accepted.
          </span>
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <Badge variant={report.imported > 0 ? 'soft-success' : 'soft-muted'}>
          {report.imported} imported
        </Badge>
        <Badge variant="soft-warning">
          <AlertTriangle className="mr-1 size-3" />
          {report.issues.length} skipped
        </Badge>
      </div>

      <div className="max-h-64 overflow-auto rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-16">Line</TableHead>
              <TableHead>What is wrong with it</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {report.issues.map((issue, index) => (
              <TableRow key={`${issue.line}-${index}`}>
                <TableCell className="at-num align-top text-xs font-medium">
                  {issue.line}
                </TableCell>
                <TableCell className="text-xs leading-relaxed">{issue.message}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <p className="text-[11px] text-muted-foreground">
        Line numbers match your spreadsheet, header row included. Fix those rows and upload
        the file again — rows that already imported are skipped rather than duplicated.
      </p>
    </div>
  );
}
